import { useParams } from 'react-router';
import { Seo } from '../components/Seo';
import { siteConfig } from '../config/site';
import { NotFoundPage } from './NotFoundPage';

type LegalDocument = {
  title: string;
  description: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
};

const documents: Record<string, LegalDocument> = {
  privacy: {
    title: 'Privacy policy',
    description: 'How website, support, commerce and licensing information is handled.',
    sections: [
      {
        heading: 'Information collected',
        paragraphs: [
          'We may receive information you provide through purchases, account registration, license activation, support requests and newsletter signup. This can include your name, email address, order details, product ownership, device activation records and the technical information you include in a support message.',
          'The website may also receive ordinary request information such as IP address, browser type, referring page and security-verification results. Optional analytics should remain disabled unless the visitor has made the required consent choice for your jurisdiction.',
        ],
      },
      {
        heading: 'Service providers',
        paragraphs: [
          'Moonbase is used for storefront, customer-account, payment, download and licensing functions. Cloudflare provides website delivery, security and serverless processing. Resend can be configured to deliver website support messages. Add every provider you actually use before publishing this policy.',
        ],
      },
      {
        heading: 'Use and retention',
        paragraphs: [
          'Information is used to complete transactions, provide downloads, issue and recover licenses, prevent abuse, answer support requests, maintain product compatibility and satisfy legal or accounting obligations. State the retention periods that match your real systems and obligations.',
        ],
      },
      {
        heading: 'Choices and requests',
        paragraphs: [
          `Customers may contact ${siteConfig.company.supportEmail} to request access, correction or deletion where applicable. Some order, tax, fraud-prevention and licensing records may need to be retained. Add the jurisdiction-specific rights and appeal process that apply to your business.`,
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of use and license',
    description: 'Website terms and a starting framework for end-user software licensing.',
    sections: [
      {
        heading: 'Account and purchases',
        paragraphs: [
          'You are responsible for accurate account information and for maintaining access to the email address used for purchase. Product prices, included formats, activation limits and update eligibility are shown at checkout or on the relevant product page.',
        ],
      },
      {
        heading: 'Software license',
        paragraphs: [
          'A purchase grants a personal, non-exclusive, non-transferable license to install and use the software within the activation allowance stated for that product. The software is licensed rather than sold. Redistribution, resale, extraction of protected components and use of the product to create a competing derivative product are prohibited except where applicable law does not permit such a restriction.',
        ],
      },
      {
        heading: 'Trials, updates and compatibility',
        paragraphs: [
          'Trial duration, update eligibility and supported operating systems may differ by product. Back up working installers before changing an important production system. No promise of indefinite compatibility with future operating systems, hosts or hardware should be made unless you intend to provide it.',
        ],
      },
      {
        heading: 'Warranty and liability',
        paragraphs: [
          'Insert warranty disclaimers, liability limits, governing law, dispute terms and mandatory consumer protections reviewed for your company and sales regions. Do not publish this draft as final legal language without qualified review.',
        ],
      },
    ],
  },
  eula: {
    title: 'End-user license agreement',
    description: 'Draft software-license terms for installation and use of Wavecraft Audio products.',
    sections: [
      {
        heading: 'License grant and activations',
        paragraphs: [
          'Subject to payment and compliance with the final agreement, the customer receives a limited, non-exclusive, non-transferable license to install and use the software within the activation allowance stated for the product. The software is licensed rather than sold.',
          'State whether one person may activate studio, laptop and replacement computers; how company or educational licenses work; and whether a license may be transferred. Keep the public product page, checkout terms and licensing-system limits consistent.',
        ],
      },
      {
        heading: 'Permitted use and restrictions',
        paragraphs: [
          'Define permitted music, film, game, broadcast, live-performance and commercial production use. Prohibit redistribution of the plugin, shared credentials, resale without an approved transfer, circumvention of licensing controls and extraction of protected code or assets, subject to rights that applicable law does not allow you to restrict.',
        ],
      },
      {
        heading: 'Ownership, updates and support',
        paragraphs: [
          'The company and its licensors retain ownership of the software, branding, documentation and included content. State which updates are included, whether major upgrades may require payment, how long installers remain available and which support channels are offered.',
        ],
      },
      {
        heading: 'Termination and project safety',
        paragraphs: [
          'Explain when a license may be suspended or terminated, how disputed activations are handled and what happens after termination. Paid perpetual licenses should have a documented outage and recovery policy that avoids unexpectedly disabling legitimate studio projects.',
        ],
      },
      {
        heading: 'Warranty, liability and governing terms',
        paragraphs: [
          'Insert jurisdiction-appropriate warranty disclaimers, liability limits, mandatory consumer rights, governing law and dispute provisions. Have the final end-user license agreement reviewed together with the checkout terms and refund policy before launch.',
        ],
      },
    ],
  },
  security: {
    title: 'Security policy',
    description: 'How to report a potential security issue affecting the website, customer accounts or software delivery.',
    sections: [
      {
        heading: 'Reporting a vulnerability',
        paragraphs: [
          `Send a clear description to ${siteConfig.company.securityEmail}. Include the affected URL or product, the steps needed to reproduce the issue, its likely impact and any supporting screenshots or logs. Do not include customer data that is not necessary to explain the report.`,
          'Replace the security address with a monitored mailbox before launch. Acknowledge good-faith reports promptly and provide status updates when a report can be reproduced.',
        ],
      },
      {
        heading: 'Good-faith research',
        paragraphs: [
          'Do not access, alter, retain or disclose another person’s data; interrupt service; use social engineering; test payment providers; distribute malware; or exploit a finding beyond the minimum needed to demonstrate it. Stop testing and report the issue when sensitive information becomes visible.',
          'Add a qualified safe-harbor statement appropriate for your company and jurisdiction before publishing this page as a formal vulnerability-disclosure policy.',
        ],
      },
      {
        heading: 'Scope',
        paragraphs: [
          'List the production domains, APIs, installers and licensing components that you operate directly. Moonbase, Cloudflare, Resend, Apple and other third-party services maintain their own security-reporting processes and should be contacted through their published channels when an issue exists solely in their systems.',
        ],
      },
      {
        heading: 'What happens next',
        paragraphs: [
          'Confirm receipt, assess severity, preserve relevant logs, develop and test a correction, and notify affected customers when required. Do not promise a bounty, public credit or a fixed remediation date unless your company has formally adopted those commitments.',
        ],
      },
    ],
  },
  refunds: {
    title: 'Refund policy',
    description: 'A customer-readable starting point for software refunds and technical-resolution steps.',
    sections: [
      {
        heading: 'Before purchase',
        paragraphs: [
          'Use the available trial and confirm the listed operating-system, processor, host and plugin-format requirements before buying. Contact support before purchase when compatibility is uncertain.',
        ],
      },
      {
        heading: 'Refund window',
        paragraphs: [
          'PLACEHOLDER: State the exact refund period, eligibility requirements and treatment of downloaded or activated digital software. Coordinate this language with Moonbase settings, consumer-law obligations and the policy shown at checkout.',
        ],
      },
      {
        heading: 'Technical problems',
        paragraphs: [
          `For a technical issue, contact ${siteConfig.company.supportEmail} with the order number, plugin version, macOS version, host application, plugin format and steps that reproduce the problem. State how long you will attempt a resolution before a refund decision.`,
        ],
      },
      {
        heading: 'Excluded cases',
        paragraphs: [
          'Define how duplicate purchases, accidental purchases, license abuse, chargebacks, subscription renewals, bundles and products bought through Apple or another marketplace are handled. Marketplace purchases usually follow that marketplace’s process.',
        ],
      },
    ],
  },
};

export function LegalPage() {
  const { document = '' } = useParams();
  const content = documents[document];
  if (!content) return <NotFoundPage />;

  return (
    <>
      <Seo title={content.title} description={content.description} path={`/legal/${document}`} />
      <section className="legal-hero section--grain">
        <div className="shell legal-hero__inner">
          <span className="eyebrow eyebrow--line">Legal information</span>
          <h1>{content.title}</h1>
          <p>{content.description}</p>
          <div className="legal-draft-banner"><strong>Draft template</strong><span>Replace business placeholders and have the final policy reviewed before launch.</span></div>
        </div>
      </section>
      <section className="section legal-content">
        <div className="shell legal-content__grid">
          <aside><span>Last updated</span><strong>August 2, 2026</strong><span>Contact</span><a href={`mailto:${siteConfig.company.supportEmail}`}>{siteConfig.company.supportEmail}</a></aside>
          <article>
            {content.sections.map((section, index) => (
              <section key={section.heading}>
                <span className="legal-section-number">0{index + 1}</span>
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </section>
            ))}
          </article>
        </div>
      </section>
    </>
  );
}
